import { useState, useEffect, useMemo } from "react";
import { Globe, Square, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ScreenshotSvg } from "@/components/walkthrough/screenshot-svg";
import {
  type ScreenshotHighlight,
  isValidBox,
} from "@/components/walkthrough/screenshot-types";
import type { WalkthroughStreamStatus } from "@/hooks/use-walkthrough-stream";
import { LiveTimeScrubber } from "./live-time-scrubber";
import { LiveZoomDialog } from "./live-zoom-dialog";
import { LiveStepTimer } from "./live-step-timer";
import { LiveScreenshotToolbar } from "./live-screenshot-toolbar";
import { LiveEmptyScreenshot } from "./live-empty-screenshot";
import type { UnifiedLiveFinding } from "./types";

export interface LiveStepDisplay {
  id: string;
  stepNumber: number;
  title: string;
  personaReasoning: string | null;
  grounding: string | null;
  screenshotPath: string | null;
}

interface LiveWalkthroughScreenshotProps {
  hostname: string;
  isLocalLive: boolean;
  runnerName?: string | null;
  displayedStep?: LiveStepDisplay;
  displayStepsCount: number;
  steps: LiveStepDisplay[];
  canEdit: boolean;
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
  isStopping: boolean;
  onStop: () => void;
  screenshotLoading: boolean;
  screenshotBlobUrl: string | null;
  activeStepIdx: number;
  onStepChange: (idx: number) => void;
  isLive: boolean;
  stepsBehindLive: number;
  onResumeLive: () => void;
  findingsByStep?: Map<number, UnifiedLiveFinding[]>;
  visibleOnMobile: boolean;
  width?: number | null;
  streamStatus?: WalkthroughStreamStatus;
  isStalled?: boolean;
  stepStartedAt?: number;
  activeActionStartedAt?: number;
}

export function LiveWalkthroughScreenshot({
  hostname,
  isLocalLive,
  runnerName,
  displayedStep,
  displayStepsCount,
  steps,
  canEdit,
  isRunning,
  isComplete,
  isErrored,
  isStopping,
  onStop,
  screenshotLoading,
  screenshotBlobUrl,
  activeStepIdx,
  onStepChange,
  isLive,
  stepsBehindLive,
  onResumeLive,
  findingsByStep,
  visibleOnMobile,
  width,
  streamStatus = "streaming",
  isStalled = false,
  stepStartedAt,
  activeActionStartedAt,
}: LiveWalkthroughScreenshotProps) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(
    null,
  );
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const isBehindLive =
    !isLive && isRunning && !isComplete && !isErrored && stepsBehindLive > 0;

  useEffect(() => {
    if (!screenshotBlobUrl) {
      setNaturalSize(null);
      return;
    }
    const img = new Image();
    img.src = screenshotBlobUrl;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
  }, [screenshotBlobUrl]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (zoomOpen) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === "h" || e.key === "H") {
        setShowHighlights((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomOpen]);

  const stepFindings = displayedStep
    ? (findingsByStep?.get(displayedStep.stepNumber) ?? [])
    : [];

  const highlights: ScreenshotHighlight[] = useMemo(() => {
    return stepFindings
      .filter((f) => isValidBox(f.targetBox))
      .map((f, i) => ({
        id: f.id,
        box: f.targetBox!,
        tone: f.type,
        label: f.title,
        index: i + 1,
      }));
  }, [stepFindings]);

  const stepAlt = `Step ${displayedStep?.stepNumber ?? 1}: ${
    displayedStep?.title ?? "Live preview"
  }`;

  return (
    <section
      aria-label="Current screenshot"
      className={cn(
        "relative flex flex-col overflow-hidden bg-surface-tonal flex-1 min-w-0 w-full",
        !visibleOnMobile && "hidden lg:flex",
      )}
      style={width != null ? { width } : undefined}
    >
      <header className="flex h-10 shrink-0 items-center justify-between gap-4 border-b border-border/60 bg-background px-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="inline-flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <Globe className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate font-mono">{hostname}</span>
          </span>

          <LiveStepTimer
            status={streamStatus}
            isStalled={isStalled}
            isRunning={isRunning}
            isComplete={isComplete}
            isErrored={isErrored}
            stepStartedAt={stepStartedAt}
            activeActionStartedAt={activeActionStartedAt}
            runnerName={runnerName}
            isLocalLive={isLocalLive}
            isBehindLive={isBehindLive}
            stepsBehindLive={stepsBehindLive}
          />
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {isBehindLive && (
            <Button
              size="xs"
              variant="outline"
              onClick={onResumeLive}
              className="gap-1 bg-foreground text-background font-semibold shadow-xs hover:bg-foreground/90"
              aria-label="Jump to live step"
            >
              <span
                aria-hidden="true"
                className="size-1.5 rounded-full bg-mark-helps animate-pulse"
              />
              Go to Live
            </Button>
          )}

          {displayedStep && (
            <span className="text-xs tabular-nums text-muted-foreground">
              Step {displayedStep.stepNumber}
              {displayStepsCount > 0 ? ` · ${displayStepsCount}` : ""}
            </span>
          )}
          {canEdit && isRunning && !isComplete && !isErrored && (
            <Button
              variant="ghost"
              size="xs"
              className="gap-1.5 text-xs text-severity-critical hover:bg-mark-critical/10 hover:text-severity-critical"
              onClick={onStop}
              disabled={isStopping}
              aria-label="Stop walkthrough"
              title={
                isLocalLive ? "The walker stops at its next step" : undefined
              }
            >
              <Square className="h-3 w-3 fill-current" aria-hidden="true" />
              {isStopping ? "Stopping…" : "Stop"}
            </Button>
          )}
        </div>
      </header>

      <div className="group relative flex flex-1 flex-col overflow-hidden">
        {screenshotLoading ? (
          <div className="flex h-full items-center justify-center">
            <Skeleton className="h-[60%] w-[80%]" />
          </div>
        ) : screenshotBlobUrl ? (
          <>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setZoomOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setZoomOpen(true);
                }
              }}
              className="relative flex h-full w-full cursor-zoom-in items-center justify-center overflow-hidden p-4 focus-visible:outline-2 focus-visible:outline-ring"
              title="Click to enlarge screenshot"
              aria-label={`${stepAlt} — Click to enlarge`}
            >
              <ScreenshotSvg
                url={screenshotBlobUrl}
                alt={stepAlt}
                highlights={highlights}
                activeHighlightId={activeHighlightId}
                showHighlights={showHighlights}
                onSelectHighlight={setActiveHighlightId}
                naturalSize={naturalSize}
                className="h-full w-full object-contain object-top"
              />
            </div>

            <LiveScreenshotToolbar
              hasHighlights={highlights.length > 0}
              showHighlights={showHighlights}
              onToggleHighlights={() => setShowHighlights((prev) => !prev)}
              onOpenZoom={() => setZoomOpen(true)}
            />
          </>
        ) : (
          <LiveEmptyScreenshot
            isRunning={isRunning && !isComplete && !isErrored}
          />
        )}
      </div>

      <LiveTimeScrubber
        steps={steps}
        activeStepIdx={activeStepIdx}
        onStepChange={onStepChange}
        isLive={isLive}
        stepsBehindLive={stepsBehindLive}
        onResumeLive={onResumeLive}
        isRunning={isRunning}
        isComplete={isComplete}
        isErrored={isErrored}
        findingsByStep={findingsByStep}
      />

      <LiveZoomDialog
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        screenshotBlobUrl={screenshotBlobUrl}
        displayedStep={displayedStep}
        highlights={highlights}
        activeHighlightId={activeHighlightId}
        showHighlights={showHighlights}
        onToggleHighlights={() => setShowHighlights((prev) => !prev)}
        onSelectHighlight={setActiveHighlightId}
        naturalSize={naturalSize}
      />
    </section>
  );
}
